// Die Gliederung eines langen Schreibens im Gespraechs-Panel (seit
// 29.09.2026, "Erst gliedern, dann entwerfen").
//
// Ein Punkt je Anliegen, Doppelungen schon zusammengefasst. ENPOWER hakt an,
// was uebernommen wird; entworfen wird nur das (VerhandlungsPassus.jsx).
// Nichts ist vorgewaehlt: Die Punkte sind Forderungen der Gegenseite, keine
// Vereinbarung. Logik und Texte stehen in src/lib/gespraech.js, der Server in
// api/_lib/verhandlungsGliederung.js.

import React, { useEffect, useState } from 'react';
import { Check, RotateCcw, Trash2 } from 'lucide-react';
import { T, karteStil, feldStil, markeStil } from '../../lib/designTokens';
import { Aufklapper, NummerKreis, textKnopfStil } from './abwaegenAnzeige';
import { GLIEDERUNG_ART, gliederungZaehler } from '../../lib/gespraech';
import { punktImVertragText } from '../../lib/imVertrag';

const ART_FARBE = {
  streichen: [T.gefahr, T.gefahrRand, T.gefahrGrund],
  aendern: [T.warn, T.warnRand, T.warnGrund],
  ergaenzen: [T.info, T.infoRand, T.infoGrund],
  hinweis: [T.muted, T.randWeich, T.grund],
};

// Laengere Vorgaben aus dem Schreiben eingeklappt - sonst stuende bei einem
// Zusatzparagraphen eine halbe Seite zwischen zwei Punkten.
const ZITAT_OFFEN_BIS = 280;

function mitStrich(farbe) {
  return farbe ? `inset 3px 0 0 ${farbe}, ${karteStil.boxShadow}` : karteStil.boxShadow;
}

/** Rueckfrage an Ort und Stelle, wie beim Aufraeumen. */
function MitRueckfrage({ frage, knopf, gesperrt, onJa, farbe = T.muted, icon = null }) {
  const [fragen, setFragen] = useState(false);
  if (!fragen) {
    return (
      <button type="button" disabled={gesperrt} onClick={() => setFragen(true)}
        style={{ ...textKnopfStil, color: farbe, opacity: gesperrt ? 0.45 : 1 }}>
        {icon}{knopf}
      </button>
    );
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <span style={{ fontSize: T.tMikro, color: T.body }}>{frage}</span>
      <button type="button" disabled={gesperrt} onClick={() => { setFragen(false); onJa(); }}
        style={{ ...textKnopfStil, color: T.gefahr }}>Ja</button>
      <button type="button" onClick={() => setFragen(false)} style={textKnopfStil}>Abbrechen</button>
    </span>
  );
}

function Zitat({ text }) {
  const lang = text.length > ZITAT_OFFEN_BIS;
  const [offen, setOffen] = useState(!lang);
  return (
    <div style={{ marginTop: 8 }}>
      {lang ? (
        <Aufklapper offen={offen} onClick={() => setOffen((v) => !v)}>Vorgegebener Wortlaut</Aufklapper>
      ) : null}
      {offen ? (
        <div style={{
          marginTop: lang ? 6 : 0, padding: '6px 10px', borderLeft: `2px solid ${T.rand}`,
          fontSize: T.tMeta, color: T.body, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>„{text}“</div>
      ) : null}
    </div>
  );
}

function Notiz({ punkt, gesperrt, onNotiz }) {
  const [wert, setWert] = useState(punkt.notiz || '');
  // Kommt ein neuer Stand vom Server (anderer Punkt, neu gegliedert), gilt er.
  useEffect(() => { setWert(punkt.notiz || ''); }, [punkt.id, punkt.notiz]);
  return (
    <textarea
      value={wert}
      onChange={(e) => setWert(e.target.value)}
      onBlur={() => { if (wert.trim() !== (punkt.notiz || '').trim()) onNotiz(wert.trim()); }}
      disabled={gesperrt}
      rows={2}
      aria-label={`Vorgabe von ENPOWER zu „${punkt.thema}“`}
      placeholder="Vorgabe von ENPOWER, optional — etwa „Indexierung nur alle 5 Jahre“"
      style={{ ...feldStil, marginTop: 8, fontSize: T.tMeta, resize: 'vertical' }}
    />
  );
}

function PunktKarte({ punkt, nummer, gesperrt, imVertrag, onWahl, onNotiz, onNochmal }) {
  const [schrift, rand, grund] = ART_FARBE[punkt.art] || ART_FARBE.hinweis;
  const strich = punkt.entworfen ? T.gut : (punkt.gewaehlt ? T.orange : '');
  const orte = [
    punkt.stelle ? `Im Vertrag: ${punkt.stelle}` : '',
    punkt.fundstellen?.length ? `Im Schreiben: ${punkt.fundstellen.join(', ')}` : '',
  ].filter(Boolean).join(' · ');
  return (
    <div style={{ ...karteStil, boxShadow: mitStrich(strich) }}>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, cursor: gesperrt ? 'default' : 'pointer' }}>
        <input
          type="checkbox"
          checked={punkt.gewaehlt}
          disabled={gesperrt}
          onChange={(e) => onWahl(e.target.checked)}
          style={{ marginTop: 4, width: 15, height: 15, accentColor: T.orange, flexShrink: 0 }}
        />
        <NummerKreis>{nummer}</NummerKreis>
        <span style={{ flex: 1, minWidth: 0, fontSize: T.tTitel, fontWeight: 700, color: T.navy, lineHeight: 1.4, paddingTop: 2 }}>
          {punkt.thema}
        </span>
        {punkt.entworfen ? (
          <span style={markeStil(T.gut, T.gutRand, T.gutGrund)}><Check size={10} /> Entworfen</span>
        ) : (
          <span style={markeStil(schrift, rand, grund)}>{GLIEDERUNG_ART[punkt.art] || punkt.art}</span>
        )}
      </label>

      <div style={{ paddingLeft: 55 }}>
        {orte ? <div style={{ marginTop: 6, fontSize: T.tMikro, color: T.muted }}>{orte}</div> : null}
        {/* "Nichts doppelt" auch hier (seit 29.09.2026): Was der Vertrag
            schon regelt, soll gar nicht erst angehakt werden. */}
        {imVertrag ? (
          <div style={{
            marginTop: 8, padding: '6px 10px', borderRadius: T.rChip, background: T.gefahrGrund,
            borderLeft: `3px solid ${T.gefahr}`, fontSize: T.tMeta, color: T.ink, lineHeight: 1.5,
          }}>
            <strong style={{ color: T.gefahr }}>Steht schon im Vertrag:</strong> {punktImVertragText(imVertrag)}
          </div>
        ) : null}
        {punkt.forderung ? (
          <div style={{ marginTop: 6, fontSize: T.tText, color: T.ink, lineHeight: 1.6 }}>{punkt.forderung}</div>
        ) : null}
        {punkt.zitat ? <Zitat text={punkt.zitat} /> : null}
        {punkt.gewaehlt ? <Notiz punkt={punkt} gesperrt={gesperrt} onNotiz={onNotiz} /> : null}
        {punkt.entworfen ? (
          <div style={{ marginTop: 6 }}>
            <button type="button" disabled={gesperrt} onClick={onNochmal}
              title="Wieder zum Entwerfen vormerken — etwa, wenn du den Entwurf verworfen hast"
              style={{ ...textKnopfStil, opacity: gesperrt ? 0.45 : 1 }}>
              <RotateCcw size={11} /> Noch einmal entwerfen
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function GliederungsListe({
  gliederung,
  gesperrt = false,
  kiBereit = true,
  // Kennung -> Befund aus punktImVertrag (src/lib/imVertrag.js).
  imVertragNach = null,
  onWahl,
  onAlle,
  onNotiz,
  onNochmal,
  onNeuGliedern,
  onVerwerfen,
}) {
  const [inOrdnungOffen, setInOrdnungOffen] = useState(false);
  const zaehler = gliederungZaehler(gliederung);
  const punkte = gliederung?.punkte || [];
  const inOrdnung = gliederung?.inOrdnung || [];
  const imVertrag = (punkt) => (imVertragNach ? imVertragNach.get(punkt.id) || null : null);
  const schonDa = punkte.filter((p) => imVertrag(p)).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {gliederung?.quelle ? (
          <div title={gliederung.quelle} style={{
            fontSize: T.tMikro, color: T.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>Aus: {gliederung.quelle}</div>
        ) : null}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', minHeight: 20 }}>
          <span style={{ fontSize: T.tMeta, fontWeight: 600, color: zaehler.gewaehlt ? T.navy : T.muted }}>
            {zaehler.gewaehlt} von {zaehler.gesamt} gewählt
            {zaehler.entworfen ? ` · ${zaehler.entworfen} entworfen` : ''}
          </span>
          {schonDa > 0 ? (
            <span title="Der Vertrag regelt das schon - beim Anhaken stünde es doppelt darin."
              style={{ fontSize: T.tMeta, fontWeight: 600, color: T.gefahr }}>
              {schonDa} schon im Vertrag
            </span>
          ) : null}
          <span style={{ flex: 1 }} />
          <button type="button" disabled={gesperrt} onClick={() => onAlle(true)}
            title={schonDa ? 'Alle wählen — außer denen, die schon im Vertrag stehen' : 'Alle wählen'}
            style={{ ...textKnopfStil, opacity: gesperrt ? 0.45 : 1 }}>Alle wählen</button>
          <button type="button" disabled={gesperrt} onClick={() => onAlle(false)}
            style={{ ...textKnopfStil, opacity: gesperrt ? 0.45 : 1 }}>Keine</button>
        </div>
        {zaehler.gewaehlt === 0 ? (
          <div style={{ fontSize: T.tMikro, color: T.muted, lineHeight: 1.55 }}>
            Nichts ist vorgewählt — es sind Forderungen der anderen Seite. Hake an, was ENPOWER übernimmt;
            nur das wird als Vertragstext entworfen.
          </div>
        ) : null}
      </div>

      {punkte.map((punkt, i) => (
        <PunktKarte
          key={punkt.id}
          punkt={punkt}
          nummer={i + 1}
          gesperrt={gesperrt}
          imVertrag={imVertrag(punkt)}
          onWahl={(gewaehlt) => onWahl(punkt.id, gewaehlt)}
          onNotiz={(notiz) => onNotiz(punkt.id, notiz)}
          onNochmal={() => onNochmal(punkt.id)}
        />
      ))}

      {inOrdnung.length > 0 ? (
        <div>
          <Aufklapper offen={inOrdnungOffen} onClick={() => setInOrdnungOffen((v) => !v)}>
            Laut Schreiben in Ordnung ({inOrdnung.length})
          </Aufklapper>
          {inOrdnungOffen ? (
            <div style={{ marginTop: 6, fontSize: T.tMeta, color: T.body, lineHeight: 1.6 }}>
              {inOrdnung.join(' · ')}
            </div>
          ) : null}
        </div>
      ) : null}

      {gliederung?.hinweis ? (
        <div style={{ fontSize: T.tMeta, color: T.muted, lineHeight: 1.55 }}>
          <strong style={{ color: T.body }}>Außerdem im Schreiben:</strong> {gliederung.hinweis}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', paddingTop: 4, borderTop: `1px solid ${T.hairline}` }}>
        <MitRueckfrage
          knopf="Neu gliedern"
          frage="Neu gliedern? Wahl und Vorgaben gehen verloren."
          gesperrt={gesperrt || !kiBereit}
          onJa={onNeuGliedern}
          icon={<RotateCcw size={11} />}
        />
        <MitRueckfrage
          knopf="Gliederung verwerfen"
          frage="Gliederung verwerfen? Entworfenes bleibt."
          gesperrt={gesperrt}
          onJa={onVerwerfen}
          icon={<Trash2 size={11} />}
        />
      </div>
    </div>
  );
}
